import { Link } from '@tiptap/extension-link';
import { mergeAttributes } from '@tiptap/core';
// @ts-ignore
import { Plugin, PluginKey } from 'prosemirror-state';

export const LinkShortenerExtension = Link.extend({
  name: 'link', // Must be 'link' to properly override the Link extension's mark type in ProseMirror schema

  addOptions() {
    return {
      ...this.parent?.(),
      shortenUrl: null,
      onProcessingChange: null,
      excludedClearTrigger: null,
    };
  },

  addStorage() {
    return {
      processingUrls: new Set(),
      excludedUrls: new Set(),
      processedShortened: new Set(),
      isProcessing: false,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      shortened: {
        default: false,
      },
      originalUrl: {
        default: null,
      },
      processing: {
        default: false,
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const realHref = HTMLAttributes.href || '';
    const isShortened = HTMLAttributes.shortened;
    const isProcessing = HTMLAttributes.processing;
    const originalUrl = HTMLAttributes.originalUrl;

    let cls = 'gh-shortener-link';
    if (isProcessing) {
      cls = 'gh-shortener-link-processing';
    } else if (isShortened) {
      cls = 'gh-shortened-link';
    }

    const attrs = mergeAttributes({ class: cls }, HTMLAttributes);
    delete attrs.target;
    delete attrs.rel;
    attrs['data-href'] = realHref;
    if (originalUrl) {
      attrs['data-original-url'] = originalUrl;
    }
    if (isProcessing) {
      attrs['data-processing'] = 'true';
      attrs['contenteditable'] = 'false';
    } else if (isShortened) {
      attrs['data-shortened'] = 'true';
      attrs['contenteditable'] = 'false';
    }
    attrs.href = 'javascript:void(0)';
    return ['a', attrs, 0];
  },

  addProseMirrorPlugins() {
    const { shortenUrl, onProcessingChange, excludedClearTrigger } = this.options as any;
    const storage = this.storage;

    const isValidUrl = (text: string) => {
      const pattern = /^(https?:\/\/)?[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z]{2,})+(:\d+)?(\/[^\s]*)?$/i;
      return pattern.test(text);
    };

    const SHORT_URL_DOMAINS = [
      'bit\\.ly', 'tinyurl\\.com', 'goo\\.gl', 't\\.co', 'ow\\.ly', 'is\\.gd', 'buff\\.ly',
      'adf\\.ly', 'bit\\.do', 'mcaf\\.ee', 'su\\.pr', 'j\\.mp', 'soo\\.gd', 'bc\\.vc',
      's2r\\.co', 'clck\\.ru', 'cutt\\.ly', 'rb\\.gy', 'shorturl\\.at', 'tiny\\.cc',
      'qr\\.net', '1url\\.com', 'hyperurl\\.co', 'urlzs\\.com', 'v\\.gd', 'x\\.co',
      'lnkd\\.in', 'db\\.tt', 'qr\\.ae', 'rebrand\\.ly', 'bl\\.ink', 'short\\.io'
    ].join('|');

    const URL_REGEX = new RegExp(
      '(' +
        'https?:\\/\\/[a-zA-Z0-9][-a-zA-Z0-9]*(\\.[a-zA-Z]{2,})+(:\\d+)?(\\/[^\\s]*)?' +
      '|' +
        '(?<![a-zA-Z0-9@])(' + SHORT_URL_DOMAINS + ')\\/[a-zA-Z0-9_\\-]+[^\\s]*' +
      '|' +
        '(?<![a-zA-Z0-9@])([a-zA-Z0-9][-a-zA-Z0-9]*\\.)+(com|org|net|io|co|ly|me|info|biz|app|dev|xyz|site|online|tech|store|shop|cloud|ai|gg|tv|fm|cc|to|in|uk|de|fr|es|it|nl|be|ch|at|au|ca|us|gov|edu)(\\/[^\\s]+)' +
      ')',
      'gi'
    );

    const shouldAutoShorten = (url: string): boolean => {
      if (!isValidUrl(url)) return false;
      const lower = url.toLowerCase();
      if (lower.includes('localhost') || lower.includes('127.0.0.1') || lower.includes('192.168.')) return false;
      if (url.length < 10) return false;
      return true;
    };

    const applyLinkMarksToRawUrls = (editor: any) => {
      if (!editor) return false;
      
      // Handle different input types: TipTap Editor, ProseMirror EditorView, or EditorState
      let view: any;
      let state: any;
      
      if (editor.view) {
        // TipTap Editor
        view = editor.view;
        state = view.state;
      } else if (editor.state && editor.dispatch) {
        // ProseMirror EditorView
        view = editor;
        state = editor.state;
      } else if (editor.doc && editor.tr) {
        // ProseMirror EditorState - can't dispatch, skip
        return false;
      } else {
        return false;
      }
      
      if (!state || !view) return false;
      
      let tr = state.tr;
      let hasChanges = false;

      state.doc.descendants((node: any, pos: number) => {
        if (node.isText) {
          const text = node.text || '';
          const hasLinkMark = node.marks.some((m: any) => m.type.name === 'link');
          if (hasLinkMark) return;

          let match;
          const urlRegex = new RegExp(URL_REGEX.source, 'gi');
          while ((match = urlRegex.exec(text)) !== null) {
            let url = match[0];
            const from = pos + match.index;
            const to = from + url.length;
            const href = url.startsWith('http') ? url : `https://${url}`;

            tr = tr.addMark(from, to, state.schema.marks.link.create({
              href: href,
              shortened: false,
              processing: false,
              originalUrl: null,
            }));
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        view.dispatch(tr);
      }
      return hasChanges;
    };

    const autoShortenLink = async (editor: any, url: string) => {
      if (!editor || !shortenUrl) return;
      if (storage.processingUrls.has(url)) return;

      // Handle different input types
      let view: any;
      let state: any;
      
      if (editor.view) {
        view = editor.view;
        state = view.state;
      } else if (editor.state && editor.dispatch) {
        view = editor;
        state = editor.state;
      } else {
        return;
      }
      
      if (!state || !view) return;

      storage.processingUrls.add(url);
      storage.isProcessing = true;
      if (onProcessingChange) onProcessingChange(true);

      let linkFrom: number | null = null;
      let linkTo: number | null = null;

      state.doc.descendants((node: any, pos: number) => {
        if (node.isText && linkFrom === null) {
          const linkMark = node.marks.find((m: any) => m.type.name === 'link');
          if (linkMark && !linkMark.attrs.shortened && !linkMark.attrs.processing) {
            const text = node.text || '';
            const nodeUrl = text.startsWith('http') ? text : `https://${text}`;
            if (nodeUrl === url || text === url) {
              linkFrom = pos;
              linkTo = pos + node.nodeSize;
            }
          }
        }
      });

      if (linkFrom === null || linkTo === null) {
        storage.processingUrls.delete(url);
        storage.isProcessing = storage.processingUrls.size > 0;
        if (onProcessingChange) onProcessingChange(storage.isProcessing);
        return;
      }

      const trProcessing = state.tr.addMark(linkFrom, linkTo, state.schema.marks.link.create({
        href: url,
        shortened: false,
        processing: true,
        originalUrl: url,
      }));
      view.dispatch(trProcessing);

      try {
        const shortUrl = await shortenUrl(url);

        const newState = view.state;
        let newFrom: number | null = null;
        let newTo: number | null = null;

        newState.doc.descendants((node: any, pos: number) => {
          if (node.isText && newFrom === null) {
            const linkMark = node.marks.find((m: any) => m.type.name === 'link');
            if (linkMark && linkMark.attrs.processing && linkMark.attrs.originalUrl === url) {
              newFrom = pos;
              newTo = pos + node.nodeSize;
            }
          }
        });

        if (newFrom !== null && newTo !== null && shortUrl) {
          const finalTr = newState.tr.replaceWith(newFrom, newTo, newState.schema.text(shortUrl, [
            newState.schema.marks.link.create({
              href: shortUrl,
              shortened: true,
              processing: false,
              originalUrl: url,
            }),
          ]));
          view.dispatch(finalTr);
          // Track both the shortened URL and the original URL to prevent re-shortening
          storage.processedShortened.add(shortUrl);
          storage.processedShortened.add(url);
        } else if (newFrom !== null && newTo !== null) {
          const revertTr = newState.tr.addMark(newFrom, newTo, newState.schema.marks.link.create({
            href: url,
            shortened: false,
            processing: false,
            originalUrl: null,
          }));
          view.dispatch(revertTr);
        }
      } catch (err) {
        console.error('Auto-shorten failed:', err);
        const errState = view.state;
        let errFrom: number | null = null;
        let errTo: number | null = null;

        errState.doc.descendants((node: any, pos: number) => {
          if (node.isText && errFrom === null) {
            const linkMark = node.marks.find((m: any) => m.type.name === 'link');
            if (linkMark && linkMark.attrs.processing && linkMark.attrs.originalUrl === url) {
              errFrom = pos;
              errTo = pos + node.nodeSize;
            }
          }
        });

        if (errFrom !== null && errTo !== null) {
          const revertTr = errState.tr.addMark(errFrom, errTo, errState.schema.marks.link.create({
            href: url,
            shortened: false,
            processing: false,
            originalUrl: null,
          }));
          view.dispatch(revertTr);
        }
      } finally {
        storage.processingUrls.delete(url);
        storage.isProcessing = storage.processingUrls.size > 0;
        if (onProcessingChange) onProcessingChange(storage.isProcessing);
      }
    };

    const scanAndShortenNewLinks = (editor: any) => {
      if (!editor) return;

      // Handle different input types
      let view: any;
      let state: any;
      
      if (editor.view) {
        view = editor.view;
        state = view.state;
      } else if (editor.state && editor.dispatch) {
        view = editor;
        state = editor.state;
      } else if (editor.doc && editor.tr) {
        // EditorState - can't dispatch, skip
        return;
      } else {
        return;
      }
      
      if (!state || !view) return;

      applyLinkMarksToRawUrls(editor);

      setTimeout(() => {
        if (!editor) return;
        
        // Re-get state after timeout as it may have changed
        let currentState: any;
        if (editor.view) {
          currentState = editor.view.state;
        } else if (editor.state) {
          currentState = editor.state;
        } else {
          return;
        }
        
        if (!currentState) return;
        
        const linksToShorten: string[] = [];

        currentState.doc.descendants((node: any) => {
          if (node.isText) {
            const linkMark = node.marks.find((m: any) => m.type.name === 'link');
            if (linkMark && !linkMark.attrs.shortened && !linkMark.attrs.processing) {
              const text = node.text || '';
              const url = text.startsWith('http') ? text : `https://${text}`;
              // Skip if this URL text is already in our processed set (either as original or shortened)
              // Also skip if the text itself looks like it was already shortened (check both text and url forms)
              if (shouldAutoShorten(url) && 
                  !storage.processingUrls.has(url) && 
                  !storage.processingUrls.has(text) &&
                  !storage.excludedUrls.has(url) && 
                  !storage.excludedUrls.has(text) &&
                  !storage.processedShortened.has(url) &&
                  !storage.processedShortened.has(text)) {
                linksToShorten.push(url);
              }
            }
          }
        });

        linksToShorten.forEach((url) => {
          autoShortenLink(editor, url);
        });
      }, 50);
    };

    storage.scan = () => scanAndShortenNewLinks(this.editor);

    const extensionThis = this;

    return [
      new Plugin({
        key: new PluginKey('linkShortener'),
        props: {
          handlePaste(view, event, slice) {
            setTimeout(() => {
              scanAndShortenNewLinks(view);
            }, 100);
            return false;
          },
        },
        appendTransaction: (transactions, oldState, newState) => {
          if (transactions.some(tr => tr.docChanged)) {
            let addedLink = false;
            transactions.forEach((tr) => {
              tr.steps.forEach((step: any) => {
                if (step.toJSON && step.toJSON().stepType === 'addMark') {
                  const mark = step.mark;
                  if (mark && mark.type && mark.type.name === 'link') {
                    addedLink = true;
                  }
                }
              });
            });
            const lastStep = transactions[transactions.length - 1]?.steps[transactions[transactions.length - 1].steps.length - 1] as any;
            if (lastStep && lastStep.slice) {
              const content = lastStep.slice.content;
              if (content && content.size === 1) {
                const text = content.firstChild?.text;
                if (text === ' ' || text === '\n') {
                  setTimeout(() => {
                    if (extensionThis.editor) {
                      scanAndShortenNewLinks(extensionThis.editor);
                    }
                  }, 500);
                }
              }
            }
            if (addedLink) {
              setTimeout(() => {
                if (extensionThis.editor) {
                  scanAndShortenNewLinks(extensionThis.editor);
                }
              }, 500);
            }
          }
          return null;
        },
      }),
    ];
  },
});